import { createServerFn } from '@tanstack/react-start';
import { env } from 'cloudflare:workers';
import {
  and,
  count as countFn,
  desc,
  eq,
  inArray,
  isNull,
  lt,
  or,
  sql,
} from 'drizzle-orm';
import { nanoid } from 'nanoid';
import { z } from 'zod';
import {
  ALL_VIDEO_MODELS,
  VIDEO_EDITOR_MODELS,
  assertVideoRequestSupported,
  calculateVideoGenerationCost,
  type VideoModelConfig,
  VideoAspectRatio,
  VideoGenerationMode,
  VideoGenerationModel,
  type VideoGenerationWorkflowType,
  VideoResolution,
  canUseVideoEndFrame,
  getVideoModelConfig,
  usesVideoReferenceImages,
} from '@/ai/video-models';
import {
  isReferenceToVideoAudioType,
  isReferenceToVideoVideoType,
  REFERENCE_TO_VIDEO_AUDIO_MAX_BYTES,
  REFERENCE_TO_VIDEO_MAX_MEDIA_SECONDS,
  REFERENCE_TO_VIDEO_VIDEO_MAX_BYTES,
  assertReferenceToVideoInputCounts,
  assertReferenceToVideoMediaDuration,
} from '@/ai/reference-to-video';
import {
  isSupportedVideoEditUpload,
  VIDEO_EDIT_MAX_FILE_SIZE,
} from '@/ai/video-edit';
import type { VideoUpscaleFactor } from '@/ai/video-upscale';
import { getUserCredits } from '@/credits/credits';
import { getDb } from '@/db';
import {
  type GenerationContinuationType,
  userFiles,
  videoGeneration,
} from '@/db/app.schema';
import { readMp4DurationSeconds } from '@/lib/video-file-metadata';
import { getBaseUrl } from '@/lib/urls';
import { authApiMiddleware } from '@/middlewares/auth-middleware';
import { assertPromptAllowedByCreem } from '@/moderation/creem';
import { getFile } from '@/storage';
import { resolveVideoReferenceFiles } from './video-reference-files-server';
import type {
  VideoEditSettings,
  VideoExtensionSettings,
  VideoFrameReference,
  VideoGenerationParams,
} from '@/workflows/video-generation';

export const INSUFFICIENT_VIDEO_CREDITS_ERROR = 'INSUFFICIENT_CREDITS';

const videoModelEnum = z.enum(
  Object.values(VideoGenerationModel) as [
    VideoGenerationModel,
    ...VideoGenerationModel[],
  ]
);

const videoAspectRatioEnum = z.enum(
  Object.values(VideoAspectRatio) as [VideoAspectRatio, ...VideoAspectRatio[]]
);

const videoResolutionEnum = z.enum(
  Object.values(VideoResolution) as [VideoResolution, ...VideoResolution[]]
);

const videoModeEnum = z.enum([
  VideoGenerationMode.NORMAL,
  VideoGenerationMode.FUN,
  VideoGenerationMode.SPICY,
] as const);

const videoWorkflowEnum = z.enum([
  'text-to-video',
  'image-to-video',
  'reference-to-video',
] as const);
const continuationTypeSchema = z.enum(['continue', 'variation', 'retry']);
const videoEditAudioSettingSchema = z.enum(['auto', 'origin']);

const videoEditInputSchema = z
  .object({
    sourceFileId: z.string().min(1).optional(),
    sourceVideoId: z.string().min(1).optional(),
    model: z.enum(VIDEO_EDITOR_MODELS),
    prompt: z
      .string()
      .min(5, 'Prompt is too short.')
      .max(20_000, 'Prompt is too long.'),
    negativePrompt: z.string().max(2000).optional(),
    referenceImageFileId: z.string().min(1).optional(),
    aspectRatio: videoAspectRatioEnum,
    resolution: videoResolutionEnum,
    audioSetting: videoEditAudioSettingSchema.default('origin'),
    promptExtend: z.boolean().default(true),
    seed: z.number().int().min(0).max(2_147_483_647).default(0),
  })
  .superRefine((value, ctx) => {
    const modelConfig = getVideoModelConfig(value.model);
    if (modelConfig && value.prompt.length > modelConfig.promptMaxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${modelConfig.displayName} supports prompts up to ${modelConfig.promptMaxLength} characters.`,
        path: ['prompt'],
      });
    }
  })
  .refine(
    (value) => Boolean(value.sourceFileId) !== Boolean(value.sourceVideoId),
    'Choose one source video.'
  );

const videoExtensionInputSchema = z.object({
  sourceVideoId: z.string().min(1),
  prompt: z
    .string()
    .min(5, 'Prompt is too short.')
    .max(20_000, 'Prompt is too long.'),
  extendSeconds: z.union([z.literal(6), z.literal(10)]),
});

const inputSchema = z
  .object({
    prompt: z
      .string()
      .min(5, 'Prompt is too short.')
      .max(20_000, 'Prompt is too long.'),
    model: videoModelEnum.default(VideoGenerationModel.GROK_IMAGINE),
    aspectRatio: videoAspectRatioEnum.default(VideoAspectRatio.TWO_TO_THREE),
    duration: z.number().int().min(1).max(30).default(6),
    resolution: videoResolutionEnum.default(VideoResolution.P480),
    mode: videoModeEnum.default(VideoGenerationMode.NORMAL),
    generateAudio: z.boolean().optional(),
    workflow: videoWorkflowEnum.default('text-to-video'),
    referenceImageFileIds: z.array(z.string().min(1)).max(9).optional(),
    referenceVideoFileIds: z.array(z.string().min(1)).max(3).optional(),
    referenceAudioFileIds: z.array(z.string().min(1)).max(3).optional(),
    startFrameFileId: z.string().min(1).optional(),
    endFrameFileId: z.string().min(1).optional(),
    parentGenerationId: z.string().min(1).optional(),
    continuationType: continuationTypeSchema.optional(),
    referenceMode: z.enum(['single', 'multi']).optional(),
  })
  .superRefine((value, ctx) => {
    const modelConfig = getVideoModelConfig(value.model);
    if (modelConfig && value.prompt.length > modelConfig.promptMaxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${modelConfig.displayName} supports prompts up to ${modelConfig.promptMaxLength} characters.`,
        path: ['prompt'],
      });
    }
    const referenceImageFileIds = value.referenceImageFileIds?.length
      ? value.referenceImageFileIds
      : [value.startFrameFileId, value.endFrameFileId].filter(
          (id): id is string => Boolean(id)
        );
    if (Boolean(value.parentGenerationId) !== Boolean(value.continuationType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Continuation action and parent generation are both required.',
        path: ['parentGenerationId'],
      });
    }
    if (
      value.workflow === 'image-to-video' &&
      referenceImageFileIds.length < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one image is required for image-to-video.',
        path: ['referenceImageFileIds'],
      });
    }
    if (
      value.workflow === 'reference-to-video' &&
      value.model === VideoGenerationModel.GROK_IMAGINE_1_5 &&
      referenceImageFileIds.length < 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one image is required for reference-to-video.',
        path: ['referenceImageFileIds'],
      });
    }
    if (
      value.workflow === 'text-to-video' &&
      (referenceImageFileIds.length > 0 ||
        (value.referenceVideoFileIds?.length ?? 0) > 0 ||
        (value.referenceAudioFileIds?.length ?? 0) > 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Reference images are only available for image-to-video and reference-to-video.',
        path: ['referenceImageFileIds'],
      });
    }
    if (
      value.workflow === 'reference-to-video' &&
      value.model !== VideoGenerationModel.GROK_IMAGINE_1_5
    ) {
      try {
        assertReferenceToVideoInputCounts({
          audioCount: value.referenceAudioFileIds?.length ?? 0,
          imageCount: referenceImageFileIds.length,
          videoCount: value.referenceVideoFileIds?.length ?? 0,
        });
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            error instanceof Error
              ? error.message
              : 'Reference files are invalid.',
          path: ['referenceImageFileIds'],
        });
      }
    }
    if (
      value.workflow === 'image-to-video' &&
      ((value.referenceVideoFileIds?.length ?? 0) > 0 ||
        (value.referenceAudioFileIds?.length ?? 0) > 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Video and audio references are only available for reference-to-video.',
        path: ['referenceVideoFileIds'],
      });
    }
  });

const statusInputSchema = z.object({
  videoId: z.string().min(1),
});

const videoDetailInputSchema = z.object({
  id: z.string().min(1),
});

const videoStatusEnum = z.enum([
  'pending',
  'processing',
  'uploading',
  'completed',
  'failed',
]);

const listVideoSchema = z.object({
  limit: z.number().int().min(1).max(60).default(20),
  cursor: z
    .string()
    .regex(/^\d+_.+$/)
    .optional(),
  model: videoModelEnum.optional(),
  resolution: videoResolutionEnum.optional(),
  aspectRatio: videoAspectRatioEnum.optional(),
  status: videoStatusEnum.optional(),
  templateId: z.string().max(64).optional(),
});

type PublicVideoModelConfig = Omit<
  VideoModelConfig,
  'kie' | 'provider' | 'providerImageModelId' | 'providerModelId'
>;

export interface VideoGenerationItem {
  id: string;
  type: VideoGenerationWorkflowType;
  prompt: string;
  model: VideoGenerationModel;
  modelLabel: string;
  aspectRatio: VideoAspectRatio;
  duration: number;
  resolution: VideoResolution;
  mode: VideoGenerationMode;
  generateAudio: boolean;
  creditsUsed: number;
  providerCreditsConsumed?: number;
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed';
  errorMessage?: string;
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  workflowInstanceId?: string;
  taskId?: string;
  outputKey?: string;
  outputUrl?: string;
  startFrameFileId?: string;
  endFrameFileId?: string;
  sourceVideoFileId?: string;
  upscaleFactor?: VideoUpscaleFactor;
  referenceImageFileIds?: string[];
  referenceVideoFileIds?: string[];
  referenceAudioFileIds?: string[];
  referenceImages?: VideoGenerationFrame[];
  startFrame?: VideoGenerationFrame;
  endFrame?: VideoGenerationFrame;
  parentGenerationId?: string;
  rootGenerationId?: string;
  continuationType?: GenerationContinuationType;
  templateId?: string;
  versionCount: number;
}

export interface VideoGenerationFrame {
  id: string;
  url: string;
  originalName: string;
}

export interface VideoGenerationPage {
  items: VideoGenerationItem[];
  hasMore: boolean;
  nextCursor: string | null;
}

export const getVideoGenerationModels = createServerFn({
  method: 'GET',
}).handler(async () => ({
  models: ALL_VIDEO_MODELS.map(toPublicVideoModelConfig),
}));

export const generateVideo = createServerFn({ method: 'POST' })
  .inputValidator(inputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const referenceFileIds = data.referenceImageFileIds?.length
      ? data.referenceImageFileIds
      : [data.startFrameFileId, data.endFrameFileId].filter(
          (id): id is string => Boolean(id)
        );
    const db = getDb();
    let parent:
      | {
          id: string;
          rootGenerationId: string | null;
        }
      | undefined;
    if (data.parentGenerationId) {
      [parent] = await db
        .select({
          id: videoGeneration.id,
          rootGenerationId: videoGeneration.rootGenerationId,
        })
        .from(videoGeneration)
        .where(
          and(
            eq(videoGeneration.id, data.parentGenerationId),
            eq(videoGeneration.userId, userId),
            isNull(videoGeneration.deletedAt)
          )
        )
        .limit(1);
      if (!parent) throw new Error('Parent generation not found.');
    }

    const referenceVideoFileIds = data.referenceVideoFileIds ?? [];
    const referenceAudioFileIds = data.referenceAudioFileIds ?? [];
    const isReferenceToVideo = data.workflow === 'reference-to-video';
    assertVideoRequestSupported({
      ...data,
      referenceImageUrls:
        data.workflow === 'image-to-video' || isReferenceToVideo
          ? referenceFileIds.map((id) => `file:${id}`)
          : undefined,
      referenceVideoUrls: isReferenceToVideo
        ? referenceVideoFileIds.map((id) => `file:${id}`)
        : undefined,
      referenceAudioUrls: isReferenceToVideo
        ? referenceAudioFileIds.map((id) => `file:${id}`)
        : undefined,
    });
    const modelConfig = getVideoModelConfig(data.model);
    if (!modelConfig) {
      throw new Error(`Unknown video model: ${data.model}`);
    }
    if (data.prompt.length > modelConfig.promptMaxLength) {
      throw new Error(
        `${modelConfig.displayName} supports prompts up to ${modelConfig.promptMaxLength} characters.`
      );
    }
    if (
      usesVideoReferenceImages(data.workflow) &&
      !modelConfig.supportsImageToVideo
    ) {
      throw new Error(
        data.workflow === 'reference-to-video'
          ? `${modelConfig.displayName} does not support reference-to-video.`
          : `${modelConfig.displayName} does not support image-to-video.`
      );
    }
    if (isReferenceToVideo && !modelConfig.supportsReferenceToVideo) {
      throw new Error(
        `${modelConfig.displayName} does not support reference-to-video.`
      );
    }
    const generateAudio = Boolean(
      data.generateAudio && modelConfig.supportsAudio
    );
    const referenceFiles =
      data.workflow === 'image-to-video' || isReferenceToVideo
        ? await resolveVideoReferenceFiles({
            allowEmpty:
              isReferenceToVideo &&
              data.model !== VideoGenerationModel.GROK_IMAGINE_1_5,
            userId,
            referenceFileIds,
          })
        : [];
    const referenceVideos = isReferenceToVideo
      ? await resolveReferenceToVideoAssets({
          allowedTypes: isReferenceToVideoVideoType,
          fileIds: referenceVideoFileIds,
          kind: 'video',
          maxBytes: REFERENCE_TO_VIDEO_VIDEO_MAX_BYTES,
          userId,
        })
      : [];
    const referenceAudios = isReferenceToVideo
      ? await resolveReferenceToVideoAssets({
          allowedTypes: isReferenceToVideoAudioType,
          fileIds: referenceAudioFileIds,
          kind: 'audio',
          maxBytes: REFERENCE_TO_VIDEO_AUDIO_MAX_BYTES,
          userId,
        })
      : [];
    const sourceDuration = isReferenceToVideo
      ? await sumReferenceVideoDurations(referenceVideos)
      : undefined;
    const cost = calculateVideoGenerationCost({
      model: data.model,
      duration: data.duration,
      resolution: data.resolution,
      generateAudio,
      workflow: data.workflow,
      sourceDuration,
      imageCount: isReferenceToVideo ? referenceFiles.length : undefined,
    });
    const balance = await getUserCredits(userId);
    if (balance.balance < cost) {
      throw new Error(
        `${INSUFFICIENT_VIDEO_CREDITS_ERROR}:${cost}:${balance.balance}`
      );
    }

    const videoId = nanoid();
    await assertPromptAllowedByCreem({
      externalId: `video:${userId}:${videoId}`,
      prompt: data.prompt,
    });

    const provider = modelConfig.provider;
    const description = `Video generation (${modelConfig.displayName}, ${data.aspectRatio}, ${data.resolution})`;

    await db.insert(videoGeneration).values({
      id: videoId,
      userId,
      type: data.workflow,
      prompt: data.prompt,
      model: data.model,
      aspectRatio: data.aspectRatio,
      duration: data.duration,
      resolution: data.resolution,
      mode: data.mode,
      startFrameFileId: referenceFiles[0]?.fileId ?? null,
      endFrameFileId:
        canUseVideoEndFrame(modelConfig) && referenceFiles.length > 1
          ? (referenceFiles[1]?.fileId ?? null)
          : null,
      sourceVideoFileId: referenceVideos[0]?.fileId ?? null,
      referenceFileIds: serializeReferenceMediaFileIds({
        audios: referenceAudioFileIds,
        images: referenceFileIds,
        videos: referenceVideoFileIds,
      }),
      parentGenerationId: parent?.id ?? null,
      rootGenerationId: parent ? (parent.rootGenerationId ?? parent.id) : null,
      continuationType:
        (data.continuationType as GenerationContinuationType | undefined) ??
        null,
      generateAudio,
      creditsUsed: cost,
      status: 'pending',
      provider,
      createdAt: new Date(),
      errorMessage: null,
    });

    let instanceId: string;
    try {
      const params: VideoGenerationParams = {
        userId,
        videoId,
        prompt: data.prompt,
        model: data.model,
        aspectRatio: data.aspectRatio,
        duration: data.duration,
        resolution: data.resolution,
        mode: data.mode,
        generateAudio,
        workflow: data.workflow,
        referenceImages: referenceFiles,
        referenceVideos,
        referenceAudios,
        startFrame: referenceFiles[0],
        endFrame: canUseVideoEndFrame(modelConfig)
          ? referenceFiles[1]
          : undefined,
        sourceVideo: referenceVideos[0],
        sourceDuration,
        imageCount: isReferenceToVideo ? referenceFiles.length : undefined,
        creditsUsed: cost,
        description,
        provider,
        baseUrl: getBaseUrl(),
        referenceMode: data.referenceMode,
      };
      const instance = await env.VIDEO_GEN_WORKFLOW.create({ params });
      instanceId = instance.id;

      await db
        .update(videoGeneration)
        .set({ workflowInstanceId: instanceId })
        .where(eq(videoGeneration.id, videoId));
    } catch (err) {
      await db.delete(videoGeneration).where(eq(videoGeneration.id, videoId));
      throw err instanceof Error
        ? err
        : new Error('Failed to start video generation.');
    }

    return {
      videoId,
      workflowInstanceId: instanceId,
      status: 'pending' as const,
      model: data.model,
      aspectRatio: data.aspectRatio,
      duration: data.duration,
      resolution: data.resolution,
      mode: data.mode,
      generateAudio,
      workflow: data.workflow,
      referenceImageFileIds: referenceFileIds,
      referenceVideoFileIds,
      referenceAudioFileIds,
      startFrameFileId: referenceFiles[0]?.fileId,
      endFrameFileId: canUseVideoEndFrame(modelConfig)
        ? referenceFiles[1]?.fileId
        : undefined,
      creditsCharged: cost,
      createdAt: Date.now(),
    };
  });

export const editVideo = createServerFn({ method: 'POST' })
  .inputValidator(videoEditInputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const db = getDb();
    const source = await resolveVideoEditSource({
      sourceFileId: data.sourceFileId,
      sourceVideoId: data.sourceVideoId,
      userId,
    });

    const model = data.model;
    const modelConfig = getVideoModelConfig(model)!;
    const supportedDurations = modelConfig.supportedDurations;
    const duration = Math.ceil(source.duration);
    if (!supportedDurations.includes(duration)) {
      const minDuration = Math.min(...supportedDurations);
      const maxDuration = Math.max(...supportedDurations);
      throw new Error(
        `${modelConfig.displayName} supports source videos from ${minDuration} to ${maxDuration} seconds.`
      );
    }
    if (!modelConfig.supportedAspectRatios.includes(data.aspectRatio)) {
      throw new Error('This aspect ratio is not supported for video editing.');
    }
    if (!modelConfig.supportedResolutions.includes(data.resolution)) {
      throw new Error(
        `${modelConfig.displayName} does not support ${data.resolution} video editing.`
      );
    }
    if (
      model === VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT &&
      data.referenceImageFileId
    ) {
      throw new Error(
        'Grok Imagine video editing does not use a reference image.'
      );
    }

    const generateAudio =
      model === VideoGenerationModel.SEEDANCE_2 && data.audioSetting === 'auto';
    const cost = calculateVideoGenerationCost({
      model,
      duration,
      resolution: data.resolution,
      generateAudio,
      sourceDuration: duration,
    });
    const balance = await getUserCredits(userId);
    if (balance.balance < cost) {
      throw new Error(
        `${INSUFFICIENT_VIDEO_CREDITS_ERROR}:${cost}:${balance.balance}`
      );
    }

    const videoId = nanoid();
    await assertPromptAllowedByCreem({
      externalId: `video-edit:${userId}:${videoId}`,
      prompt: data.prompt,
    });

    const referenceFiles = data.referenceImageFileId
      ? await resolveVideoReferenceFiles({
          userId,
          referenceFileIds: [data.referenceImageFileId],
        })
      : [];
    const sourceVideo: VideoFrameReference = {
      contentType: source.file.contentType,
      fileId: source.file.id,
      r2Key: source.file.r2Key,
    };
    const editSettings: VideoEditSettings = {
      audioSetting: data.audioSetting,
      negativePrompt: data.negativePrompt?.trim() || undefined,
      preserveDuration: true,
      promptExtend: data.promptExtend,
      seed: data.seed,
    };
    const description = `Video edit (${modelConfig.displayName}, ${data.aspectRatio}, ${data.resolution})`;

    await db.insert(videoGeneration).values({
      id: videoId,
      userId,
      type: 'image-to-video',
      prompt: data.prompt.trim(),
      model,
      aspectRatio: data.aspectRatio,
      duration,
      resolution: data.resolution,
      mode: VideoGenerationMode.NORMAL,
      sourceVideoFileId: source.file.id,
      referenceFileIds: data.referenceImageFileId
        ? JSON.stringify([data.referenceImageFileId])
        : null,
      parentGenerationId: source.parent?.id ?? null,
      rootGenerationId: source.parent
        ? (source.parent.rootGenerationId ?? source.parent.id)
        : null,
      continuationType: 'edit',
      generateAudio,
      creditsUsed: cost,
      status: 'pending',
      provider: modelConfig.provider,
      createdAt: new Date(),
      errorMessage: null,
    });

    let instanceId: string;
    try {
      const params: VideoGenerationParams = {
        userId,
        videoId,
        prompt: data.prompt.trim(),
        model,
        aspectRatio: data.aspectRatio,
        duration,
        resolution: data.resolution,
        mode: VideoGenerationMode.NORMAL,
        generateAudio,
        workflow: 'image-to-video',
        referenceImages: referenceFiles,
        sourceVideo,
        editSettings,
        creditsUsed: cost,
        description,
        provider: modelConfig.provider,
        baseUrl: getBaseUrl(),
      };
      const instance = await env.VIDEO_GEN_WORKFLOW.create({ params });
      instanceId = instance.id;
      await db
        .update(videoGeneration)
        .set({ workflowInstanceId: instanceId })
        .where(eq(videoGeneration.id, videoId));
    } catch (err) {
      await db.delete(videoGeneration).where(eq(videoGeneration.id, videoId));
      throw err instanceof Error
        ? err
        : new Error('Failed to start video editing.');
    }

    return {
      videoId,
      workflowInstanceId: instanceId,
      status: 'pending' as const,
      model,
      aspectRatio: data.aspectRatio,
      duration,
      resolution: data.resolution,
      mode: VideoGenerationMode.NORMAL,
      generateAudio,
      workflow: 'image-to-video' as const,
      creditsCharged: cost,
      createdAt: Date.now(),
    };
  });

interface ResolvedVideoEditSource {
  duration: number;
  file: {
    contentType: string;
    id: string;
    r2Key: string;
    size: number;
  };
  parent?: {
    id: string;
    rootGenerationId: string | null;
  };
}

async function resolveVideoEditSource({
  sourceFileId,
  sourceVideoId,
  userId,
}: {
  sourceFileId?: string;
  sourceVideoId?: string;
  userId: string;
}): Promise<ResolvedVideoEditSource> {
  const db = getDb();

  if (sourceVideoId) {
    const [generation] = await db
      .select({
        duration: videoGeneration.duration,
        id: videoGeneration.id,
        outputVideoKey: videoGeneration.outputVideoKey,
        rootGenerationId: videoGeneration.rootGenerationId,
        status: videoGeneration.status,
      })
      .from(videoGeneration)
      .where(
        and(
          eq(videoGeneration.id, sourceVideoId),
          eq(videoGeneration.userId, userId),
          isNull(videoGeneration.deletedAt)
        )
      )
      .limit(1);

    if (
      !generation ||
      generation.status !== 'completed' ||
      !generation.outputVideoKey
    ) {
      throw new Error('The source video must finish saving before editing.');
    }

    const [file] = await db
      .select()
      .from(userFiles)
      .where(
        and(
          eq(userFiles.userId, userId),
          eq(userFiles.r2Key, generation.outputVideoKey)
        )
      )
      .limit(1);
    if (!file) throw new Error('The saved source video was not found.');
    validateVideoEditFile(file);

    return {
      duration: generation.duration,
      file,
      parent: generation,
    };
  }

  const [file] = await db
    .select()
    .from(userFiles)
    .where(and(eq(userFiles.id, sourceFileId!), eq(userFiles.userId, userId)))
    .limit(1);
  if (!file) throw new Error('The uploaded source video was not found.');
  validateVideoEditFile(file);

  const stored = await getFile(file.r2Key);
  if (!stored) throw new Error('The uploaded source video was not found.');
  const duration = await readMp4DurationSeconds(
    await new Response(stored.body).arrayBuffer()
  ).catch(() => {
    throw new Error('Upload an MP4 or MOV file with readable video metadata.');
  });

  return { duration, file };
}

function validateVideoEditFile(file: { contentType: string; size: number }) {
  if (!isSupportedVideoEditUpload(file.contentType)) {
    throw new Error('Video editing supports MP4 and MOV files.');
  }
  if (file.size > VIDEO_EDIT_MAX_FILE_SIZE) {
    throw new Error('Video editing supports source files up to 50MB.');
  }
}

export const extendVideo = createServerFn({ method: 'POST' })
  .inputValidator(videoExtensionInputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const db = getDb();
    const [source] = await db
      .select({
        id: videoGeneration.id,
        aspectRatio: videoGeneration.aspectRatio,
        duration: videoGeneration.duration,
        generateAudio: videoGeneration.generateAudio,
        mode: videoGeneration.mode,
        model: videoGeneration.model,
        provider: videoGeneration.provider,
        resolution: videoGeneration.resolution,
        rootGenerationId: videoGeneration.rootGenerationId,
        status: videoGeneration.status,
        taskId: videoGeneration.taskId,
        type: videoGeneration.type,
      })
      .from(videoGeneration)
      .where(
        and(
          eq(videoGeneration.id, data.sourceVideoId),
          eq(videoGeneration.userId, userId),
          isNull(videoGeneration.deletedAt)
        )
      )
      .limit(1);

    if (source?.model === VideoGenerationModel.GROK_IMAGINE_1_5) {
      throw new Error(
        'Grok Imagine 1.5 continues from the final frame. Refresh this page and try again.'
      );
    }

    const eligibleModels = new Set<VideoGenerationModel>([
      VideoGenerationModel.GROK_IMAGINE,
    ]);
    if (
      !source ||
      source.status !== 'completed' ||
      source.provider !== 'kie' ||
      !source.taskId ||
      !eligibleModels.has(source.model)
    ) {
      throw new Error(
        'Choose a completed Grok Imagine video generated in this account.'
      );
    }

    const modelConfig = getVideoModelConfig(source.model);
    if (modelConfig && data.prompt.length > modelConfig.promptMaxLength) {
      throw new Error(
        `${modelConfig.displayName} supports prompts up to ${modelConfig.promptMaxLength} characters.`
      );
    }

    const cost = calculateVideoGenerationCost({
      model: source.model,
      duration: data.extendSeconds,
      resolution: source.resolution,
      generateAudio: source.generateAudio,
    });
    const balance = await getUserCredits(userId);
    if (balance.balance < cost) {
      throw new Error(
        `${INSUFFICIENT_VIDEO_CREDITS_ERROR}:${cost}:${balance.balance}`
      );
    }

    const videoId = nanoid();
    await assertPromptAllowedByCreem({
      externalId: `video-extend:${userId}:${videoId}`,
      prompt: data.prompt,
    });

    const extension: VideoExtensionSettings = {
      sourceTaskId: source.taskId,
      extendAt: source.duration,
      extendSeconds: data.extendSeconds,
    };
    const description = `Video extension (${modelConfig?.displayName ?? source.model}, +${data.extendSeconds}s)`;
    const totalDuration = source.duration + data.extendSeconds;

    await db.insert(videoGeneration).values({
      id: videoId,
      userId,
      type: source.type,
      prompt: data.prompt.trim(),
      model: source.model,
      aspectRatio: source.aspectRatio,
      duration: totalDuration,
      resolution: source.resolution,
      mode: source.mode,
      parentGenerationId: source.id,
      rootGenerationId: source.rootGenerationId ?? source.id,
      continuationType: 'continue',
      generateAudio: source.generateAudio,
      creditsUsed: cost,
      status: 'pending',
      provider: source.provider,
      createdAt: new Date(),
      errorMessage: null,
    });

    let instanceId: string;
    try {
      const params: VideoGenerationParams = {
        userId,
        videoId,
        prompt: data.prompt.trim(),
        model: source.model,
        aspectRatio: source.aspectRatio,
        duration: data.extendSeconds,
        resolution: source.resolution,
        mode: source.mode,
        generateAudio: source.generateAudio,
        workflow: source.type,
        extension,
        creditsUsed: cost,
        description,
        provider: source.provider,
        baseUrl: getBaseUrl(),
      };
      const instance = await env.VIDEO_GEN_WORKFLOW.create({ params });
      instanceId = instance.id;
      await db
        .update(videoGeneration)
        .set({ workflowInstanceId: instanceId })
        .where(eq(videoGeneration.id, videoId));
    } catch (err) {
      await db.delete(videoGeneration).where(eq(videoGeneration.id, videoId));
      throw err instanceof Error
        ? err
        : new Error('Failed to start video extension.');
    }

    return {
      videoId,
      workflowInstanceId: instanceId,
      status: 'pending' as const,
      model: source.model,
      aspectRatio: source.aspectRatio,
      duration: totalDuration,
      resolution: source.resolution,
      mode: source.mode,
      generateAudio: source.generateAudio,
      workflow: source.type,
      creditsCharged: cost,
      createdAt: Date.now(),
    };
  });

export const getVideoGenerationStatus = createServerFn({ method: 'GET' })
  .inputValidator(statusInputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const rows = await db
      .select()
      .from(videoGeneration)
      .where(eq(videoGeneration.id, data.videoId))
      .limit(1);

    const row = rows[0];
    if (!row || row.userId !== context.userId) {
      throw new Error('Video generation not found');
    }

    const outputUrl = row.outputVideoKey
      ? `/api/storage/file?key=${encodeURIComponent(row.outputVideoKey)}`
      : (row.outputExternalUrl ?? undefined);

    return {
      videoId: row.id,
      status: row.status,
      workflowInstanceId: row.workflowInstanceId,
      taskId: row.taskId,
      outputKey: row.outputVideoKey,
      outputUrl,
      errorMessage: row.errorMessage,
      errorCategory: row.errorCategory,
      creditsUsed: row.creditsUsed,
      providerCreditsConsumed: row.providerCreditsConsumed ?? undefined,
      model: row.model,
      aspectRatio: row.aspectRatio,
      duration: row.duration,
      resolution: row.resolution,
      mode: row.mode,
      generateAudio: row.generateAudio,
      workflow: row.type,
      startFrameFileId: row.startFrameFileId ?? undefined,
      endFrameFileId: row.endFrameFileId ?? undefined,
      sourceVideoFileId: row.sourceVideoFileId ?? undefined,
      upscaleFactor: row.upscaleFactor ?? undefined,
      createdAt: row.createdAt,
      startedAt: row.startedAt,
      completedAt: row.completedAt,
    };
  });

export const getVideoGeneration = createServerFn({ method: 'GET' })
  .inputValidator(videoDetailInputSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }): Promise<VideoGenerationItem | null> => {
    const db = getDb();
    const rows = await db
      .select()
      .from(videoGeneration)
      .where(
        and(
          eq(videoGeneration.id, data.id),
          eq(videoGeneration.userId, context.userId),
          isNull(videoGeneration.deletedAt)
        )
      )
      .limit(1);

    const row = rows[0];
    if (!row) return null;
    const [frames, versionCounts] = await Promise.all([
      getVideoGenerationFrames(row, context.userId),
      getVideoVersionCounts([row], context.userId),
    ]);
    return toVideoGenerationItem(
      row,
      frames,
      versionCounts.get(row.rootGenerationId ?? row.id) ?? 1
    );
  });

export const listVideoGenerations = createServerFn({ method: 'GET' })
  .inputValidator(listVideoSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }): Promise<VideoGenerationPage> => {
    const db = getDb();
    const cursor = parseVideoCursor(data.cursor);
    const cursorClause = cursor
      ? or(
          lt(videoGeneration.createdAt, new Date(cursor.createdAt)),
          and(
            eq(videoGeneration.createdAt, new Date(cursor.createdAt)),
            lt(videoGeneration.id, cursor.id)
          )
        )
      : undefined;

    const filters = [
      eq(videoGeneration.userId, context.userId),
      isNull(videoGeneration.deletedAt),
    ];
    if (cursorClause) filters.push(cursorClause);
    if (data.model) filters.push(eq(videoGeneration.model, data.model));
    if (data.resolution)
      filters.push(eq(videoGeneration.resolution, data.resolution));
    if (data.aspectRatio)
      filters.push(eq(videoGeneration.aspectRatio, data.aspectRatio));
    if (data.status) filters.push(eq(videoGeneration.status, data.status));
    if (data.templateId)
      filters.push(eq(videoGeneration.templateId, data.templateId));

    const rows = await db
      .select()
      .from(videoGeneration)
      .where(and(...filters))
      .orderBy(desc(videoGeneration.createdAt), desc(videoGeneration.id))
      .limit(data.limit + 1);

    const hasMore = rows.length > data.limit;
    const pageRows = hasMore ? rows.slice(0, data.limit) : rows;
    const last = pageRows[pageRows.length - 1];
    const nextCursor =
      hasMore && last ? `${toVideoMs(last.createdAt)}_${last.id}` : null;
    const versionCounts = await getVideoVersionCounts(pageRows, context.userId);

    return {
      items: pageRows.map((row) =>
        toVideoGenerationItem(
          row,
          undefined,
          versionCounts.get(row.rootGenerationId ?? row.id) ?? 1
        )
      ),
      hasMore,
      nextCursor,
    };
  });

function parseVideoCursor(cursor: string | undefined) {
  if (!cursor) return null;
  const sep = cursor.indexOf('_');
  if (sep === -1) return null;
  const createdAt = Number(cursor.slice(0, sep));
  const id = cursor.slice(sep + 1);
  if (!Number.isFinite(createdAt) || !id) return null;
  return { createdAt, id };
}

function toVideoMs(value: Date | number): number {
  return value instanceof Date ? value.getTime() : value;
}

function buildVideoFileUrl(key: string | null | undefined) {
  if (!key) return undefined;
  return `/api/storage/file?key=${encodeURIComponent(key)}`;
}

function toVideoGenerationItem(
  row: typeof videoGeneration.$inferSelect,
  frames?: {
    referenceImages?: VideoGenerationFrame[];
    startFrame?: VideoGenerationFrame;
    endFrame?: VideoGenerationFrame;
  },
  versionCount = 1
): VideoGenerationItem {
  const modelConfig = getVideoModelConfig(row.model);
  const parsedReferenceMedia = parseReferenceMediaFileIds(row);

  return {
    id: row.id,
    type: row.type,
    prompt: row.prompt,
    model: row.model,
    modelLabel: modelConfig?.displayName ?? row.model,
    aspectRatio: row.aspectRatio,
    duration: row.duration,
    resolution: row.resolution,
    mode: row.mode,
    generateAudio: row.generateAudio,
    creditsUsed: row.creditsUsed,
    providerCreditsConsumed: row.providerCreditsConsumed ?? undefined,
    status: row.status,
    errorMessage: row.errorMessage ?? undefined,
    createdAt: toVideoMs(row.createdAt),
    startedAt: row.startedAt ? toVideoMs(row.startedAt) : undefined,
    completedAt: row.completedAt ? toVideoMs(row.completedAt) : undefined,
    workflowInstanceId: row.workflowInstanceId ?? undefined,
    taskId: row.taskId ?? undefined,
    outputKey: row.outputVideoKey ?? undefined,
    outputUrl:
      buildVideoFileUrl(row.outputVideoKey) ??
      row.outputExternalUrl ??
      undefined,
    startFrameFileId: row.startFrameFileId ?? undefined,
    endFrameFileId: row.endFrameFileId ?? undefined,
    sourceVideoFileId: row.sourceVideoFileId ?? undefined,
    upscaleFactor: row.upscaleFactor ?? undefined,
    referenceImageFileIds: parsedReferenceMedia.images,
    referenceVideoFileIds: parsedReferenceMedia.videos,
    referenceAudioFileIds: parsedReferenceMedia.audios,
    referenceImages: frames?.referenceImages,
    startFrame: frames?.startFrame,
    endFrame: frames?.endFrame,
    parentGenerationId: row.parentGenerationId ?? undefined,
    rootGenerationId: row.rootGenerationId ?? undefined,
    continuationType: row.continuationType ?? undefined,
    templateId: row.templateId ?? undefined,
    versionCount,
  };
}

async function getVideoVersionCounts(
  rows: Array<typeof videoGeneration.$inferSelect>,
  userId: string
) {
  const rootIds = Array.from(
    new Set(rows.map((row) => row.rootGenerationId ?? row.id))
  );
  if (rootIds.length === 0) return new Map<string, number>();

  const canonicalRoot = sql<string>`coalesce(${videoGeneration.rootGenerationId}, ${videoGeneration.id})`;
  const counts = await getDb()
    .select({ rootId: canonicalRoot, count: countFn() })
    .from(videoGeneration)
    .where(
      and(
        eq(videoGeneration.userId, userId),
        isNull(videoGeneration.deletedAt),
        or(
          inArray(videoGeneration.id, rootIds),
          inArray(videoGeneration.rootGenerationId, rootIds)
        )
      )
    )
    .groupBy(canonicalRoot);

  return new Map(counts.map((item) => [item.rootId, Number(item.count)]));
}

async function getVideoGenerationFrames(
  row: typeof videoGeneration.$inferSelect,
  userId: string
) {
  const ids = getVideoReferenceFileIds(row);
  if (ids.length === 0) return undefined;

  const files = await getDb()
    .select({
      id: userFiles.id,
      originalName: userFiles.originalName,
      r2Key: userFiles.r2Key,
    })
    .from(userFiles)
    .where(and(eq(userFiles.userId, userId), inArray(userFiles.id, ids)));
  const byId = new Map(files.map((file) => [file.id, file]));
  const toFrame = (id: string | null) => {
    if (!id) return undefined;
    const file = byId.get(id);
    if (!file) return undefined;
    return {
      id: file.id,
      originalName: file.originalName,
      url: buildVideoFileUrl(file.r2Key)!,
    };
  };
  return {
    referenceImages: ids
      .map((id) => toFrame(id))
      .filter((frame): frame is VideoGenerationFrame => Boolean(frame)),
    startFrame: toFrame(row.startFrameFileId),
    endFrame: toFrame(row.endFrameFileId),
  };
}

async function resolveReferenceToVideoAssets({
  allowedTypes,
  fileIds,
  kind,
  maxBytes,
  userId,
}: {
  allowedTypes: (contentType: string) => boolean;
  fileIds: string[];
  kind: 'audio' | 'video';
  maxBytes: number;
  userId: string;
}): Promise<VideoFrameReference[]> {
  if (fileIds.length === 0) return [];
  const requestedIds = Array.from(new Set(fileIds));
  const rows = await getDb()
    .select({
      id: userFiles.id,
      r2Key: userFiles.r2Key,
      contentType: userFiles.contentType,
      size: userFiles.size,
    })
    .from(userFiles)
    .where(
      and(inArray(userFiles.id, requestedIds), eq(userFiles.userId, userId))
    );
  if (rows.length !== requestedIds.length) {
    throw new Error(`One or more reference ${kind} files were not found.`);
  }
  const byId = new Map(rows.map((row) => [row.id, row]));
  return fileIds.map((fileId) => {
    const file = byId.get(fileId);
    if (!file) {
      throw new Error(`One or more reference ${kind} files were not found.`);
    }
    if (!allowedTypes(file.contentType)) {
      throw new Error(
        kind === 'video'
          ? 'Reference videos must be MP4 or MOV.'
          : 'Reference audio must be MP3 or WAV.'
      );
    }
    if (file.size > maxBytes) {
      throw new Error(
        kind === 'video'
          ? 'Videos must be smaller than 50MB.'
          : 'Audio files must be smaller than 15MB.'
      );
    }
    return {
      contentType: file.contentType,
      fileId: file.id,
      r2Key: file.r2Key,
    };
  });
}

async function sumReferenceVideoDurations(videos: VideoFrameReference[]) {
  if (videos.length === 0) return 0;
  const durations = await Promise.all(
    videos.map(async (video) => {
      const stored = await getFile(video.r2Key);
      if (!stored) {
        throw new Error('A reference video was not found in storage.');
      }
      const duration = await readMp4DurationSeconds(
        await new Response(stored.body).arrayBuffer()
      ).catch(() => {
        throw new Error(
          'Upload an MP4 or MOV file with readable video metadata.'
        );
      });
      assertReferenceToVideoMediaDuration(duration);
      return duration;
    })
  );
  const total = durations.reduce((sum, duration) => sum + duration, 0);
  if (total > REFERENCE_TO_VIDEO_MAX_MEDIA_SECONDS) {
    throw new Error('Reference videos cannot exceed 15 seconds in total.');
  }
  return Math.ceil(total);
}

function serializeReferenceMediaFileIds({
  audios,
  images,
  videos,
}: {
  audios: string[];
  images: string[];
  videos: string[];
}) {
  if (images.length === 0 && videos.length === 0 && audios.length === 0) {
    return null;
  }
  if (videos.length === 0 && audios.length === 0) {
    return JSON.stringify(images);
  }
  return JSON.stringify({ images, videos, audios });
}

function parseReferenceMediaFileIds(row: typeof videoGeneration.$inferSelect): {
  audios: string[];
  images: string[];
  videos: string[];
} {
  if (row.referenceFileIds) {
    try {
      const parsed = JSON.parse(row.referenceFileIds) as unknown;
      if (Array.isArray(parsed)) {
        return {
          audios: [],
          images: parsed.filter((id): id is string => typeof id === 'string'),
          videos: row.sourceVideoFileId ? [row.sourceVideoFileId] : [],
        };
      }
      if (parsed && typeof parsed === 'object') {
        const record = parsed as {
          audios?: unknown;
          images?: unknown;
          videos?: unknown;
        };
        const asIds = (value: unknown) =>
          Array.isArray(value)
            ? value.filter((id): id is string => typeof id === 'string')
            : [];
        return {
          audios: asIds(record.audios),
          images: asIds(record.images),
          videos: asIds(record.videos),
        };
      }
    } catch {
      // Fall back to the legacy frame columns for historical rows.
    }
  }
  return {
    audios: [],
    images: [row.startFrameFileId, row.endFrameFileId].filter(
      (id): id is string => Boolean(id)
    ),
    videos: row.sourceVideoFileId ? [row.sourceVideoFileId] : [],
  };
}

function getVideoReferenceFileIds(
  row: typeof videoGeneration.$inferSelect
): string[] {
  return parseReferenceMediaFileIds(row).images;
}

function toPublicVideoModelConfig(
  config: VideoModelConfig
): PublicVideoModelConfig {
  const {
    kie: _kie,
    provider: _provider,
    providerImageModelId: _providerImageModelId,
    providerModelId: _providerModelId,
    ...publicConfig
  } = config;
  return publicConfig;
}
